/**
 * Neutral form-state surface backed by React Hook Form.
 *
 * `FormContext` is the only shape the form system exposes to field
 * callbacks, `visible` predicates, custom renderers, and the `useForm`
 * `ref`. Nothing here is named after the underlying library so consumers
 * stay decoupled from it.
 */
import { createContext, useContext } from 'react'
import {
  useController,
  useFormContext as useReactHookFormContext,
  useWatch,
  type UseFormReturn,
  type FieldErrors,
} from 'react-hook-form'
import type { FormContext } from './generated-types'

type UploadFileFn = (args: {
  file: File
  use_case: string
}) => Promise<{ uuid?: string; UUID?: string; file_name?: string; fileName?: string } & Record<string, unknown>>

const UploadContext = createContext<UploadFileFn | null>(null)
export const UploadProvider = UploadContext.Provider
export function useUpload(): UploadFileFn | null {
  return useContext(UploadContext)
}

/** Flatten React Hook Form's nested error tree into a `{ path: message }` map. */
export function flattenErrors(errors: FieldErrors): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  const walk = (node: unknown, prefix: string) => {
    if (!node || typeof node !== 'object') return
    const message = (node as { message?: unknown }).message
    if (typeof message === 'string') {
      out[prefix] = message
      return
    }
    for (const [key, child] of Object.entries(node as Record<string, unknown>)) {
      if (key === 'ref' || key === 'type') continue
      walk(child, prefix ? `${prefix}.${key}` : key)
    }
  }
  for (const [key, node] of Object.entries(errors)) walk(node, key)
  return out
}

/**
 * Build a `FormContext` from a React Hook Form instance. `values` is
 * supplied by the caller so it can pass a reactive snapshot (via
 * `useWatch`) where reactivity matters. `submit` wires `submitForm()` to
 * the form's real submit pipeline; when omitted (field-level callbacks
 * never submit) it is a no-op.
 */
export function makeFormContext(
  methods: UseFormReturn<Record<string, unknown>>,
  values: Record<string, unknown>,
  submit?: () => Promise<void>,
): FormContext {
  return {
    values,
    errors: flattenErrors(methods.formState.errors),
    isValid: Object.keys(methods.formState.errors).length === 0,
    isSubmitting: methods.formState.isSubmitting,
    setFieldValue: (field, value) =>
      methods.setValue(field, value as never, { shouldDirty: true, shouldValidate: false }),
    setFieldError: (field, message) =>
      message ? methods.setError(field, { type: 'manual', message }) : methods.clearErrors(field),
    setValues: (next) => methods.reset(next, { keepErrors: true }),
    submitForm: submit ?? (async () => {}),
    validateForm: async () => {
      await methods.trigger()
      return flattenErrors(methods.formState.errors)
    },
    resetForm: (next) => methods.reset(next?.values),
  }
}

/**
 * Reactive neutral form context for components rendered inside a form
 * (field callbacks, `visible` predicates, custom renderers). Re-renders
 * when any value changes so reads of `ctx.values` are always current.
 */
export function useFormContext(): FormContext {
  const methods = useReactHookFormContext<Record<string, unknown>>()
  const values = (useWatch({ control: methods.control }) as Record<string, unknown>) ?? {}
  return makeFormContext(methods, values)
}

export interface FieldStateResult {
  value: unknown
  error: string | undefined
  touched: boolean
  setValue: (value: unknown) => void
  clearError: () => void
  markTouched: () => void
}

/** Per-field state, mapping RHF's controller into a small neutral shape. */
export function useFieldState(name: string): FieldStateResult {
  const methods = useReactHookFormContext<Record<string, unknown>>()
  const { field, fieldState, formState } = useController({ name, control: methods.control })
  return {
    value: field.value,
    error: fieldState.error?.message,
    // Mirror the prior behaviour: a field counts as touched once the form
    // is submitted, so required errors surface even without a blur.
    touched: fieldState.isTouched || formState.isSubmitted,
    setValue: (value) => field.onChange(value),
    clearError: () => methods.clearErrors(name),
    markTouched: () => methods.setValue(name, methods.getValues(name) as never, { shouldTouch: true }),
  }
}
